import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  DollarSign,
  Flame,
  MessageSquare,
  Phone,
  PhoneCall,
  PhoneIncoming,
} from 'lucide-react';
import { PageHeader, Button, MetricCard, EmptyState } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './SourcesPage.module.css';
import { useSourceAttribution } from '../hooks/useCallSources';
import { useCallAgents } from '../hooks/useCallAgents';
import { useCallQueueActions } from '../hooks/useCallLog';
import { LoadError } from '../components/LoadError';
import { channelLabel } from '../components/channelLabel';
import type { AttributionCall, HandledOutcome } from '../api/callsApi';

const OBJECTION_SUGGESTIONS: Record<string, string> = {
  price: 'Suggested callback: lead with financing options and the no-obligation consult.',
  insurance: 'Suggested callback: confirm their carrier is accepted and offer to verify coverage.',
  timing: 'Suggested callback: offer to hold a specific window that fits their schedule.',
};

const COMMITMENT_PILL: Record<string, { label: string; className: string }> = {
  booked_intent: { label: 'Booked intent', className: 'pillSuccess' },
  callback_requested: { label: 'Callback requested', className: 'pillWarning' },
  not_committed: { label: 'No commit', className: 'pillError' },
  not_a_lead: { label: 'Not a lead', className: 'pillMuted' },
};

function money(n: number | null | undefined): string {
  if (!n) return '—';
  return `$${n.toLocaleString('en-US')}`;
}

function timeAgo(iso: string): string {
  const mins = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function commitmentPill(commitment: string | null) {
  const p = commitment ? COMMITMENT_PILL[commitment] : null;
  if (!p) return <span>—</span>;
  const cls = (patterns as Record<string, string>)[p.className] ?? '';
  return <span className={cls}>{p.label}</span>;
}

const HANDLED_CHOICES: { value: HandledOutcome; label: string }[] = [
  { value: 'booked', label: 'Booked' },
  { value: 'not_booked', label: 'Not booked' },
  { value: 'not_reached', label: 'Not reached' },
];

function HotLeadCard({ lead }: { lead: AttributionCall }) {
  const { updateQueue } = useCallQueueActions();
  const pendingOutcome = updateQueue.isPending ? updateQueue.variables?.handledOutcome : null;
  const suggestion = lead.objection ? OBJECTION_SUGGESTIONS[lead.objection] : null;
  const callNumber = lead.callback ?? lead.callerNumber;

  return (
    <div
      className={`${styles.alertCard} ${lead.leadTemperature === 'warm' ? styles.alertCardWarm : ''}`}
    >
      <div className={styles.alertHeader}>
        {lead.caller ?? lead.callerNumber ?? 'Unknown caller'}
        {lead.estimatedValue ? (
          <span className={patterns.pillError}>{money(lead.estimatedValue)}</span>
        ) : null}
        <span className={styles.alertTime}>{timeAgo(lead.receivedAt)}</span>
      </div>
      {(lead.reason || lead.serviceInterest) && (
        <div className={styles.alertBody}>
          {lead.reason ?? `Interested in ${lead.serviceInterest}.`}
          {lead.objection ? (
            <>
              {' '}
              Hesitated on <strong>{lead.objection}</strong>.
            </>
          ) : null}
        </div>
      )}
      {suggestion && <div className={styles.alertSuggestion}>{suggestion}</div>}
      <div className={styles.alertActions}>
        {callNumber && (
          <Button
            size="sm"
            leftIcon={<Phone size={12} />}
            onClick={() => {
              window.location.href = `tel:${callNumber}`;
            }}
          >
            Call {callNumber}
          </Button>
        )}
        {/* The outcome feeds conversion stats — ask which one instead of
            assuming every handled lead booked. */}
        {HANDLED_CHOICES.map((c) => (
          <Button
            key={c.value}
            size="sm"
            variant="secondary"
            loading={pendingOutcome === c.value}
            disabled={updateQueue.isPending}
            onClick={() => updateQueue.mutate({ id: lead.id, handledOutcome: c.value })}
          >
            {c.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function SourcesPage() {
  const { attribution, isLoading, isError, refetch, isFetching } = useSourceAttribution();
  const { agents } = useCallAgents();
  const navigate = useNavigate();

  const campaignAgent = agents.find((a) => a.templateKey === 'campaign');
  const goBuilder = () =>
    navigate(campaignAgent ? `/calls/agents/${campaignAgent.id}` : '/calls/agents');

  const totals = attribution?.totals;
  const sources = attribution?.sources ?? [];
  const hotLeads = attribution?.hotLeads ?? [];
  const recentCalls = attribution?.recentCalls ?? [];
  const temperature = attribution?.temperature;

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Campaign Responder — Sources"
        subtitle="Every call attributed to the campaign that made the phone ring. Last 30 days."
        actions={
          <div className={styles.alertActions}>
            <Button variant="secondary" leftIcon={<ArrowLeft size={13} />} onClick={goBuilder}>
              Open builder
            </Button>
          </div>
        }
      />

      {isError ? (
        <LoadError
          title="Couldn't load campaign sources"
          onRetry={() => refetch()}
          isRetrying={isFetching}
        />
      ) : (
        <>
          <div className={`${patterns.statGrid4} ${styles.statsRow}`}>
            <MetricCard title="Campaign calls" value={totals?.calls ?? '—'} icon={PhoneCall} />
            <MetricCard
              title="Leads captured"
              value={totals?.leads ?? '—'}
              icon={MessageSquare}
              tone="green"
              sub={
                totals?.calls
                  ? `${Math.round((totals.leads / totals.calls) * 100)}% of calls`
                  : undefined
              }
            />
            <MetricCard
              title="Booked intent"
              value={totals?.bookedIntent ?? '—'}
              icon={PhoneIncoming}
              tone="blue"
              sub={
                totals?.leads
                  ? `${Math.round(((totals.bookedIntent ?? 0) / totals.leads) * 100)}% of leads`
                  : undefined
              }
            />
            <MetricCard
              title="Est. opportunity value"
              value={totals ? money(totals.estimatedValue) : '—'}
              icon={DollarSign}
              sub="from service interest — never counted as revenue"
            />
          </div>

          <div className={styles.layout}>
            <div>
              <div className={patterns.gridTable}>
                <div
                  className={`${patterns.gridTableHeader} ${styles.perfGridCols} ${styles.stackedHeader}`}
                >
                  <span>Source</span>
                  <span>Calls</span>
                  <span>Leads</span>
                  <span>Booked intent</span>
                  <span>Conversion</span>
                  <span>Est. value</span>
                  <span title="Campaign spend to date ÷ all leads from this source">
                    Cost / lead (all time)
                  </span>
                </div>
                {!isLoading && sources.length === 0 && (
                  <EmptyState
                    icon={<PhoneIncoming size={20} />}
                    title="No sources yet"
                    description="Create sources on your Campaign Responder agent — each gets its own tracked number and shows up here with per-campaign performance."
                  />
                )}
                {sources.map((s) => (
                  <div key={s.id} className={`${patterns.gridTableRow} ${styles.perfGridCols}`}>
                    <div>
                      <div className={styles.sourceName}>{s.name}</div>
                      <div className={styles.sourceMeta}>
                        {channelLabel(s.channel)}
                        {s.campaignName ? ` · linked to ${s.campaignName}` : ' · not linked'}
                      </div>
                    </div>
                    <div className={patterns.cellSecondary} data-label="Calls">
                      {s.calls}
                    </div>
                    <div className={patterns.cellSecondary} data-label="Leads">
                      {s.leads}
                    </div>
                    <div className={patterns.cellSecondary} data-label="Booked intent">
                      {s.bookedIntent}
                    </div>
                    <div className={styles.conversionCell} data-label="Conversion">
                      <progress
                        className={styles.bar}
                        max={100}
                        value={s.conversionPct}
                        aria-label={`Conversion ${s.conversionPct}%`}
                      />
                      {s.conversionPct}%
                    </div>
                    <div className={styles.sourceName} data-label="Est. value">
                      {money(s.estimatedValue)}
                    </div>
                    <div className={patterns.cellSecondary} data-label="Cost / lead (all time)">
                      {s.costPerLead != null ? money(s.costPerLead) : '—'}
                    </div>
                  </div>
                ))}
              </div>

              {sources.some((s) => s.campaignName) && (
                <p className={styles.emptyHint}>
                  Linked sources also appear on the campaign ROI report in Ad Campaigns — spend next
                  to calls, leads, and value.
                </p>
              )}

              <div className={patterns.gridTable}>
                <div
                  className={`${patterns.gridTableHeader} ${styles.recentGridCols} ${styles.stackedHeader}`}
                >
                  <span>Caller</span>
                  <span>Source</span>
                  <span>Service interest</span>
                  <span>Outcome</span>
                  <span>Est. value</span>
                  <span>Time</span>
                </div>
                {recentCalls.length === 0 && (
                  <EmptyState
                    icon={<PhoneIncoming size={20} />}
                    title="No campaign calls yet"
                    description="Calls to source numbers appear here with full attribution."
                  />
                )}
                {recentCalls.map((c) => (
                  <div key={c.id} className={`${patterns.gridTableRow} ${styles.recentGridCols}`}>
                    <div>
                      <div className={styles.sourceName}>
                        {c.caller ?? c.callerNumber ?? 'Unknown'}
                      </div>
                      {c.patientStatus && (
                        <div className={styles.sourceMeta}>{c.patientStatus}</div>
                      )}
                    </div>
                    <div className={patterns.cellSecondary} data-label="Source">
                      {c.sourceName ?? '—'}
                    </div>
                    <div className={patterns.cellSecondary} data-label="Service interest">
                      {c.serviceInterest ?? '—'}
                    </div>
                    <div>{commitmentPill(c.commitment)}</div>
                    <div className={patterns.cellSecondary} data-label="Est. value">
                      {money(c.estimatedValue)}
                    </div>
                    <div className={styles.sourceMeta}>{timeAgo(c.receivedAt)}</div>
                  </div>
                ))}
              </div>
            </div>

            <aside className={styles.rail}>
              <div className={`${patterns.panel}`}>
                <div className={patterns.kicker}>
                  <Flame size={12} /> Hot leads — call back now
                </div>
                {hotLeads.length === 0 ? (
                  <p className={styles.emptyHint}>
                    When a promising caller doesn't book, they show up here with what they asked and
                    why they hesitated.
                  </p>
                ) : (
                  hotLeads.map((lead) => <HotLeadCard key={lead.id} lead={lead} />)
                )}
                <p className={styles.emptyHint}>
                  Alerts fire within minutes of the call. Practices that call back within the hour
                  win back roughly 25–30% of these.
                </p>
              </div>

              <div className={`${patterns.panel}`}>
                <div className={patterns.kicker}>Lead temperature</div>
                <div className={styles.kvRow}>
                  <span>Hot — ready to book</span>
                  <span className={styles.kvValue}>{temperature?.hot ?? 0}</span>
                </div>
                <div className={styles.kvRow}>
                  <span>Warm — this month</span>
                  <span className={styles.kvValue}>{temperature?.warm ?? 0}</span>
                </div>
                <div className={styles.kvRow}>
                  <span>Researching</span>
                  <span className={styles.kvValue}>{temperature?.cool ?? 0}</span>
                </div>
                <div className={styles.kvRow}>
                  <span>Not a lead / spam</span>
                  <span className={styles.kvValue}>{temperature?.notALead ?? 0}</span>
                </div>
              </div>
            </aside>
          </div>

          <p className={styles.footnote}>
            Values are estimated opportunity from your value table — attribution is lead-level,
            never claimed as revenue.
          </p>
        </>
      )}
    </div>
  );
}
